import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from 'tss-react/mui';
import { keyframes } from 'tss-react';
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ButtonBase from "@mui/material/ButtonBase";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import {
  CONNECTED, INCOMING, ON_HOLD,
  statusText, sessionName, callTime, primarySession,
} from "../utils/callSessions";

// Ongoing call, shown in the app bar while the user is on another page.
// Clicking it returns to the Phone view; the call can be ended (or an
// incoming one answered) without leaving the page.

const pulse = keyframes({
  "0%": { boxShadow: "0 0 0 0 rgba(255, 255, 255, 0.55)" },
  "70%": { boxShadow: "0 0 0 7px rgba(255, 255, 255, 0)" },
  "100%": { boxShadow: "0 0 0 0 rgba(255, 255, 255, 0)" },
});

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    height: 38,
    marginRight: theme.spacing(1),
    paddingRight: theme.spacing(0.5),
    borderRadius: 19,
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.16)",
  },
  open: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    height: "100%",
    padding: theme.spacing(0, 0.75, 0, 1.5),
    borderRadius: 19,
    color: "inherit",
    font: "inherit",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: "#ffd27a",
  },
  dotActive: {
    backgroundColor: "#6ee28f",
  },
  dotHold: {
    backgroundColor: "rgba(255, 255, 255, 0.6)",
  },
  dotPulsing: {
    animation: `${pulse} 1.6s ease-out infinite`,
  },
  text: {
    display: "flex",
    alignItems: "baseline",
    gap: 6,
    fontSize: 14,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  name: {
    fontWeight: 500,
    maxWidth: 180,
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("sm")]: {
      maxWidth: 84,
    },
  },
  detail: {
    opacity: 0.85,
    fontSize: 13,
  },
  // The status word is dropped on small screens; the dot and timer remain.
  status: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  action: {
    width: 30,
    height: 30,
    color: "#fff",
    "& svg": {
      fontSize: 18,
    },
  },
  end: {
    backgroundColor: "#e5322d",
    "&:hover": {
      backgroundColor: "#e5322d",
      filter: "brightness(0.92)",
    },
  },
  answer: {
    backgroundColor: "#1faa4b",
    "&:hover": {
      backgroundColor: "#1faa4b",
      filter: "brightness(0.92)",
    },
  },
}));

const CallIndicator = ({ sessions, onOpen, onHangUp, onAnswer }) => {
  const { classes, cx } = useStyles();

  // Re-render once a second while a call is connected, for the timer.
  const hasTimedCall = (sessions || []).some((session) => session.connectedAt);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!hasTimedCall) return undefined;
    const id = window.setInterval(() => setTick((tick) => tick + 1), 1000);
    return () => window.clearInterval(id);
  }, [hasTimedCall]);

  if (!sessions || sessions.length === 0) return null;

  const session = primarySession(sessions);
  const status = session.sessionStatus;
  const incoming = status === INCOMING;
  const others = sessions.length - 1;
  const time = callTime(session);
  const name = sessionName(session);

  return (
    <div className={classes.root} role="status" aria-label={`${statusText(status)}: ${name}`}>
      <Tooltip title="Go to the call">
        <ButtonBase className={classes.open} onClick={onOpen} aria-label="go to the call">
          <span
            className={cx(
              classes.dot,
              status === CONNECTED && classes.dotActive,
              status === ON_HOLD && classes.dotHold,
              status !== ON_HOLD && classes.dotPulsing,
            )}
          />
          <span className={classes.text}>
            <span className={classes.name}>{name}</span>
            <span className={classes.detail}>
              <span className={classes.status}>{statusText(status)}{time ? " · " : ""}</span>
              {time}
              {others > 0 ? ` · +${others}` : ""}
            </span>
          </span>
        </ButtonBase>
      </Tooltip>
      {incoming && (
        <Tooltip title="Answer">
          <IconButton
            aria-label="answer call"
            className={cx(classes.action, classes.answer)}
            onClick={() => onAnswer(session.remoteNumber)}
            size="small">
            <CallIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={incoming ? "Decline" : "End call"}>
        <IconButton
          aria-label={incoming ? "decline call" : "end call"}
          className={cx(classes.action, classes.end)}
          onClick={() => onHangUp(session.remoteNumber)}
          size="small">
          <CallEndIcon />
        </IconButton>
      </Tooltip>
    </div>
  );
};

CallIndicator.propTypes = {
  sessions: PropTypes.array,
  onOpen: PropTypes.func.isRequired,
  onHangUp: PropTypes.func.isRequired,
  onAnswer: PropTypes.func.isRequired,
};

export default CallIndicator;
